import React, { useEffect, useRef, useState } from 'react';
import { ArrowIcon } from './ArrowIcon';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import './Hero.scss';

const Hero: React.FC = () => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [glow, setGlow] = useState({ x: 400, y: 400, active: false });
  const [parallax, setParallax] = useState(() =>
    prefersReducedMotion ? 0 : Math.min(window.scrollY * 0.15, 140)
  );
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Skip parallax entirely under reduced motion (apple-design §14) rather
    // than just softening it — a moving background is exactly what to avoid.
    if (prefersReducedMotion) {
      setParallax(0);
      return;
    }
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setParallax(Math.min(window.scrollY * 0.15, 140));
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [prefersReducedMotion]);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (prefersReducedMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setGlow({ x: e.clientX - rect.left, y: e.clientY - rect.top, active: true });
  };

  const handleMouseLeave = () => setGlow((g) => ({ ...g, active: false }));

  return (
    <section
      id="top"
      className="hero"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="hero__glow"
        style={{ left: glow.x, top: glow.y, opacity: glow.active ? 1 : 0 }}
      />
      <div className="hero__grid" style={{ transform: `translateY(${parallax}px)` }} />
      <div className="hero__numeral" style={{ transform: `translateY(${parallax}px)` }}>
        01
      </div>

      <div className="hero__inner section-inner">
        <div className="hero__eyebrow">
          <span className="hero__dot" />
          <span>LIVE PASSIONATELY, ADVENTURE OFTEN</span>
        </div>

        <h1 className="hero__headline">
          <span className="hero__line hero__line--1">Innovate, build,</span>
          <span className="hero__line hero__line--2">disrupt.</span>
        </h1>

        <div className="hero__foot">
          <p className="hero__lede">
            I like my coffee black, I write{' '}
            <a
              href="https://open.spotify.com/track/6eLZQCuorGhpIaWwSVZUaP?si=c025bb19f9764d48"
              target="_blank"
              rel="noopener noreferrer"
              data-cursor-hover
            >
              rock ballads
            </a>
            , and I design with a mad level of precision – challenging the status quo
            with meaning and resolve to drive creative destruction.
          </p>

          <a
            href="https://www.linkedin.com/in/ryanchongzx/"
            target="_blank"
            rel="noopener noreferrer"
            className="hero__cta"
            data-cursor-hover
          >
            Connect with me
            <ArrowIcon />
          </a>
        </div>
      </div>

      <div className="hero__scroll">
        <span className="hero__scroll-line" />
        Scroll
      </div>
    </section>
  );
};

export default Hero;
